// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Alias Resolver Service
// Bridges messy user inputs with canonical ingredient catalog.
// Flow:
//   1. Try exact match against existing confirmed aliases
//   2. Try exact match against canonical names
//   3. Fall back to fuzzy match (returns suggestions for user confirmation)
// ══════════════════════════════════════════════════════════════════════════════

import { supabase } from '@lib/supabase';
import { normalizeText, findBestMatches, classifyConfidence } from '@utils/fuzzyMatcher';

export class AliasResolver {
  /**
   * Main resolution entry point.
   * @param {string} rawText - User's messy input (e.g., "حليب المراعي 1L")
   * @param {string} tenantId
   * @returns {Promise<ResolutionResult>}
   */
  static async resolve(rawText, tenantId) {
    const normalized = normalizeText(rawText);
    if (!normalized) {
      return { status: 'empty', input: rawText };
    }

    // Step 1: Check confirmed aliases for this tenant
    const { data: existingAlias } = await supabase
      .from('ingredient_aliases')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('alias_normalized', normalized)
      .eq('is_confirmed', true)
      .maybeSingle();

    if (existingAlias) {
      // Increment occurrence counter
      await supabase
        .from('ingredient_aliases')
        .update({ occurrences: (existingAlias.occurrences || 0) + 1 })
        .eq('id', existingAlias.id);

      return {
        status: 'resolved',
        confidence: 'high',
        confidenceScore: parseFloat(existingAlias.confidence),
        canonical: {
          id: existingAlias.canonical_ingredient_id,
          name_ar: existingAlias.canonical_name_ar,
          name_en: existingAlias.canonical_name_en
        },
        source: 'alias_exact',
        input: rawText,
        alias: existingAlias
      };
    }

    // Step 2: Try exact match against canonical ingredient names
    const { data: ingredients } = await supabase
      .from('ingredients')
      .select('id, name_ar, name_en')
      .eq('tenant_id', tenantId);

    const canonicalList = ingredients || [];
    const exactMatch = canonicalList.find(ing =>
      normalizeText(ing.name_ar) === normalized ||
      normalizeText(ing.name_en) === normalized
    );

    if (exactMatch) {
      return {
        status: 'resolved',
        confidence: 'high',
        confidenceScore: 1.0,
        canonical: exactMatch,
        source: 'canonical_exact',
        input: rawText
      };
    }

    // Step 3: Fuzzy match — produce ranked suggestions
    const candidates = canonicalList.flatMap(ing => [
      { ingredient: ing, text: ing.name_ar, lang: 'ar' },
      { ingredient: ing, text: ing.name_en, lang: 'en' }
    ]);

    const fuzzyMatches = findBestMatches(rawText, candidates, {
      threshold: 0.60,
      topN: 5,
      getCandidateText: (c) => c.text
    });

    if (fuzzyMatches.length === 0) {
      return {
        status: 'unresolved',
        confidence: 'none',
        input: rawText,
        suggestions: []
      };
    }

    // Deduplicate by ingredient id, keep highest score
    const dedupedMap = new Map();
    for (const { candidate, score } of fuzzyMatches) {
      const existing = dedupedMap.get(candidate.ingredient.id);
      if (!existing || existing.score < score) {
        dedupedMap.set(candidate.ingredient.id, { candidate, score });
      }
    }
    const suggestions = Array.from(dedupedMap.values()).map(({ candidate, score }) => ({
      canonical: candidate.ingredient,
      matchedText: candidate.text,
      score,
      confidence: classifyConfidence(score)
    }));

    const top = suggestions[0];
    // If top suggestion has high confidence, mark as auto-resolvable
    if (top.confidence === 'high') {
      return {
        status: 'auto_matched',
        confidence: top.confidence,
        confidenceScore: top.score,
        canonical: top.canonical,
        source: 'fuzzy_high',
        input: rawText,
        suggestions
      };
    }

    return {
      status: 'needs_confirmation',
      confidence: top.confidence,
      input: rawText,
      suggestions
    };
  }

  /**
   * Persist a confirmed mapping so future occurrences are resolved instantly.
   */
  static async confirmMapping({ tenantId, rawText, canonical, source = 'manual', score = 1.0 }) {
    const normalized = normalizeText(rawText);
    const { data, error } = await supabase
      .from('ingredient_aliases')
      .upsert({
        tenant_id: tenantId,
        canonical_ingredient_id: canonical.id,
        canonical_name_ar: canonical.name_ar,
        canonical_name_en: canonical.name_en,
        alias_text: rawText,
        alias_normalized: normalized,
        source,
        confidence: score,
        is_confirmed: true,
        occurrences: 1
      }, { onConflict: 'tenant_id,alias_normalized' })
      .select()
      .single();
    return { data, error };
  }

  /**
   * Reject a suggested mapping — creates an unconfirmed record so we don't
   * re-suggest the same pairing.
   */
  static async rejectSuggestion({ tenantId, rawText }) {
    const normalized = normalizeText(rawText);
    const { error } = await supabase
      .from('ingredient_aliases')
      .delete()
      .eq('tenant_id', tenantId)
      .eq('alias_normalized', normalized)
      .eq('is_confirmed', false);
    return { error };
  }

  /**
   * Bulk resolve — for batch processing uploaded invoice line items.
   */
  static async resolveBatch(rawTexts, tenantId) {
    const results = await Promise.all(
      rawTexts.map(text => this.resolve(text, tenantId))
    );
    return results;
  }

  /**
   * Get all unconfirmed aliases that need user review
   */
  static async getPendingMappings(tenantId) {
    const { data, error } = await supabase
      .from('ingredient_aliases')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('is_confirmed', false)
      .order('occurrences', { ascending: false });
    return { data: data || [], error };
  }

  /**
   * Get all confirmed mappings (catalog)
   */
  static async getConfirmedMappings(tenantId) {
    const { data, error } = await supabase
      .from('ingredient_aliases')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('is_confirmed', true)
      .order('occurrences', { ascending: false });
    return { data: data || [], error };
  }
}

export default AliasResolver;
